// components/MaharashtraMapView.jsx
import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaArrowLeft } from "react-icons/fa";
import PropTypes from "prop-types";

// Default marker setup
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIconPng,
    shadowUrl: markerShadowPng,
});

const MaharashtraMapView = ({
    geojsonPath = "/geojson/1.geojson",
    title = "Maharashtra Map",
    dataKey = "immun_rate",
    colorRange = ["#dbeafe", "#1e3a8a"],
    onDistrictSelect,
    showLegend = true,
    dataMap = {},
    height = "500px",   // ✅ NEW PROP ADDED
}) => {
    const mapRef = useRef();
    const [districtsGeoJSON, setDistrictsGeoJSON] = useState(null);
    const [districtView, setDistrictView] = useState(null);

    useEffect(() => {
        fetch(process.env.PUBLIC_URL + geojsonPath)
            .then((res) => res.json())
            .then((data) => setDistrictsGeoJSON(data))
            .catch((err) => console.error("GeoJSON load error:", err));
    }, [geojsonPath]);

    // Color helpers
    const hexToRgb = (hex) => {
        const c = hex.replace("#", "");
        const n = parseInt(c, 16);
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };
    const rgbToHex = ({ r, g, b }) =>
        `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
    const interpolate = (a, b, t) => Math.round(a + (b - a) * t);
    const interpolateColor = (a, b, t) => {
        const A = hexToRgb(a);
        const B = hexToRgb(b);
        return rgbToHex({
            r: interpolate(A.r, B.r, t),
            g: interpolate(A.g, B.g, t),
            b: interpolate(A.b, B.b, t),
        });
    };

    const getColor = (val) => {
        const v = Math.max(0, Math.min(100, Number(val || 0)));
        return interpolateColor(colorRange[0], colorRange[1], v / 100);
    };

    const computeStyle = (feature) => {
        const name =
            feature.properties?.DISTRICT ||
            feature.properties?.district ||
            feature.properties?.name;

        const val = dataMap[name] ?? Math.random() * 100;

        return {
            fillColor: getColor(val),
            color: "#ffffff",
            weight: 1,
            fillOpacity: 0.8,
        };
    };

    const onEachDistrict = (feature, layer) => {
        const name =
            feature.properties?.DISTRICT ||
            feature.properties?.district ||
            feature.properties?.name;

        const val = dataMap[name] ?? 0;

        layer.bindTooltip(`${name}<br/>${title}: ${val}%`, { sticky: true });

        layer.on({
            click: () => {
                setDistrictView(name);
                if (onDistrictSelect) onDistrictSelect(name);
            },
        });
    };

    const handleBack = () => {
        setDistrictView(null);
        if (onDistrictSelect) onDistrictSelect(null);
    };

    return (
        <div className="relative bg-white rounded-lg shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-base font-semibold text-slate-800">{title}</h4>

                {districtView && (
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
                    >
                        <FaArrowLeft /> Back
                    </button>
                )}
            </div>

            {/* ✅ Apply Dynamic Height */}
            <div
                className="w-full rounded-md overflow-hidden border"
                style={{ height }}
            >
                <MapContainer
                    center={[19.7515, 75.7139]}
                    zoom={6.5}
                    minZoom={6}
                    maxZoom={10}
                    style={{ height: "100%", width: "100%" }}
                    whenCreated={(map) => (mapRef.current = map)}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OSM"
                    />

                    {districtsGeoJSON && (
                        <GeoJSON
                            data={districtsGeoJSON}
                            style={computeStyle}
                            onEachFeature={onEachDistrict}
                        />
                    )}
                </MapContainer>

                {showLegend && (
                    <div className="absolute bottom-3 right-3 bg-white bg-opacity-90 shadow-lg p-3 rounded-md text-xs space-y-1">
                        <div className="font-semibold">{title}</div>
                        {[0, 20, 40, 60, 80, 100].map((val) => (
                            <div key={val} className="flex items-center gap-2">
                                <span
                                    style={{
                                        background: getColor(val),
                                        width: 20,
                                        height: 10,
                                        borderRadius: 2,
                                    }}
                                ></span>
                                <span>{val}%</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

MaharashtraMapView.propTypes = {
    geojsonPath: PropTypes.string,
    title: PropTypes.string,
    dataKey: PropTypes.string,
    colorRange: PropTypes.array,
    onDistrictSelect: PropTypes.func,
    showLegend: PropTypes.bool,
    dataMap: PropTypes.object,
    height: PropTypes.string, // ✅ ADDED
};

export default MaharashtraMapView;
